import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Loader2, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sectors = [
  { value: 'not-decided', label: 'I have not decided a sector yet' },
  { value: 'mining', label: 'Mining, Minerals & Diamonds' },
  { value: 'green-energy', label: 'Green Energy (Solar & Renewables)' },
  { value: 'agriculture', label: 'Agriculture & Agro-Processing' },
  { value: 'ict', label: 'ICT & Digital Infrastructure' },
  { value: 'automotive', label: 'Automotive & Manufacturing' },
  { value: 'business-expansion', label: 'Business Expansion & Partnerships' },
  { value: 'other', label: 'Other (please specify below)' },
];

const commonQuestions = {
  attraction: {
    label: 'What is attracting you to Africa as a market?',
    options: [
      'I have seen the opportunity through media, content or trusted sources and want to explore further',
      'I am looking to diversify my current business internationally and Africa seems promising',
      'I have heard from a peer or associate who is already active in Africa',
      'I have a product or service that I believe has demand in Africa but am not sure where to start'
    ]
  },
  currentBusiness: {
    label: 'What is your current business, and what sector does it belong to?',
    options: [
      'Manufacturing — I produce goods and am looking for new markets or raw material sources',
      'Trading — I buy and sell commodities or finished goods',
      'Services — I offer professional, technical or business services',
      'Investor — I am looking to place capital in high-growth markets'
    ]
  },
  investmentBudget: {
    label: 'What is the approximate investment budget you are open to committing for Africa?',
    options: [
      'Under INR 50 Lakhs — I am at an early exploratory stage',
      'INR 50 Lakhs to 2 Crore — I am serious but want guidance before committing',
      'INR 2 Crore to 10 Crore — I am capital-ready and looking for the right entry point',
      'Above INR 10 Crore — I am a significant investor and need the right strategic partner'
    ]
  },
  regionPreference: {
    label: 'Which part of Africa are you most drawn to, if at all?',
    options: [
      'East Africa — Kenya, Tanzania, Ethiopia, Uganda',
      'West Africa — Nigeria, Ghana, Ivory Coast, Senegal',
      'Southern Africa — South Africa, Zambia, Zimbabwe, Mozambique',
      'Central Africa — DRC, Cameroon, Congo',
      'North Africa — Egypt, Morocco, Tunisia',
      'I have no preference yet — open to guidance on the best region for my goals'
    ]
  },
  visitedAfrica: {
    label: 'Have you ever visited Africa — for business or personal travel?',
    options: [
      'Yes — I have visited and have some ground-level awareness',
      'No — but I am actively researching and planning a visit soon',
      'No — I am completely new and rely on expert guidance to assess this'
    ]
  },
  biggestConcern: {
    label: 'What is the single biggest concern or question that is holding you back from deciding?',
    options: [
      'I do not know which sector to enter and need strategic direction first',
      'I am uncertain about the legal, regulatory and company setup process in Africa',
      'I do not have the right contacts or access to decision-makers on the ground',
      'I am unsure about the financial risk and how to protect my investment',
      'I need a trusted partner who has done this before and can guide me end to end'
    ]
  },
  exploringWith: {
    label: 'Are you exploring this alone or with a business partner / co-investor?',
    options: [
      'Alone — this is my personal initiative',
      'With a business partner — we are exploring this jointly',
      'As a company — I represent a corporate entity evaluating Africa expansion',
      'As part of a consortium or investor group'
    ]
  },
  timeline: {
    label: 'What is your ideal timeline to take the first concrete step?',
    options: [
      'Within the next 3 months — I am ready to move now',
      '3 to 6 months — I want to gather more information first',
      '6 to 12 months — I am planning ahead but not in a rush',
      'No fixed timeline — I am exploring with no deadline'
    ]
  }
};

export const QuestionnairePage = ({ onBack }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Section A - Contact Details
    fullName: '',
    email: '',
    phone: '',
    company: '',
    designation: '',
    city: '',
    
    // Section B - Sector
    sector: '',
    otherSector: '',
    
    // Section C - Common Questions
    attraction: '',
    currentBusiness: '',
    businessDescription: '',
    investmentBudget: '',
    regionPreference: '',
    visitedAfrica: '',
    biggestConcern: '',
    exploringWith: '',
    timeline: '',
    
    // Declaration
    declaration: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/questionnaire`, formData);
      setIsSubmitted(true);
      toast.success('Thank you! Your questionnaire has been submitted successfully.');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.fullName && formData.email && formData.phone;
    }
    if (step === 2) {
      return formData.sector && (formData.sector !== 'other' || formData.otherSector);
    }
    if (step === 3) {
      return formData.attraction && formData.investmentBudget && formData.timeline;
    }
    if (step === 4) {
      return formData.declaration;
    }
    return true;
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg text-center"
        >
          <div className="w-20 h-20 rounded-full bg-[#50C878]/20 flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-[#50C878]" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4 font-['Playfair_Display']">
            Thank You!
          </h2>
          <p className="text-white/60 mb-8">
            Your questionnaire has been submitted successfully. We will review your responses and get back to you within 48 hours to schedule your Discovery Call.
          </p>
          <button onClick={onBack} className="btn-primary">
            Return to Homepage
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-16 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/60 hover:text-[#D4AF37] mb-6 transition-colors"
          >
            <ArrowLeft size={18} />
            <span>Back to Homepage</span>
          </button>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 font-['Playfair_Display']">
            Business Enquiry <span className="gradient-text">Questionnaire</span>
          </h1>
          <p className="text-white/60">
            Africa Business Expansion | Sector-Specific Intake Form
          </p>
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {['Contact Details', 'Sector', 'Questions', 'Declaration'].map((label, idx) => (
              <span
                key={label}
                className={`text-xs ${step > idx ? 'text-[#D4AF37]' : step === idx + 1 ? 'text-white' : 'text-white/30'}`}
              >
                {label}
              </span>
            ))}
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#D4AF37]"
              initial={{ width: 0 }}
              animate={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Steps */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="glass rounded-2xl p-6 sm:p-8"
        >
          {/* Step 1: Contact Details */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2">Section A</p>
                <h2 className="text-xl font-semibold text-white mb-6">Your Contact Details</h2>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/60 block mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    className="input-modern"
                    placeholder="Enter your full name"
                    data-testid="input-fullname"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 block mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="input-modern"
                    placeholder="your@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/60 block mb-2">Phone / WhatsApp *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="input-modern"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 block mb-2">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="input-modern"
                    placeholder="Your city"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/60 block mb-2">Company / Business Name</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="input-modern"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 block mb-2">Designation / Role</label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => handleChange('designation', e.target.value)}
                    className="input-modern"
                    placeholder="Your role"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Sector Selection */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2">Section B</p>
                <h2 className="text-xl font-semibold text-white mb-2">Select Your Sector of Interest</h2>
                <p className="text-sm text-white/50">Choose the area you are exploring for Africa — this determines the questions that follow</p>
              </div>
              
              <div className="space-y-3">
                {sectors.map((sector) => (
                  <label
                    key={sector.value}
                    className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                      formData.sector === sector.value 
                        ? 'bg-[#D4AF37]/10 border-2 border-[#D4AF37]' 
                        : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                    }`}
                  >
                    <input
                      type="radio"
                      name="sector"
                      value={sector.value}
                      checked={formData.sector === sector.value}
                      onChange={(e) => handleChange('sector', e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.sector === sector.value ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-white/30'
                    }`}>
                      {formData.sector === sector.value && <Check size={12} className="text-[#0A0A0A]" />}
                    </div>
                    <span className="text-white/80">{sector.label}</span>
                  </label>
                ))}
              </div>
              
              {formData.sector === 'other' && (
                <div>
                  <label className="text-sm text-white/60 block mb-2">Please specify your sector *</label>
                  <input
                    type="text"
                    value={formData.otherSector}
                    onChange={(e) => handleChange('otherSector', e.target.value)}
                    className="input-modern"
                    placeholder="Describe your sector"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 3: Common Questions */}
          {step === 3 && (
            <div className="space-y-8">
              <div>
                <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2">Section C</p>
                <h2 className="text-xl font-semibold text-white mb-2">Tell Us More</h2>
                <p className="text-sm text-white/50">Answer a few questions to help us prepare for your Discovery Call</p>
              </div>

              {/* Attraction Question */}
              <div>
                <label className="text-sm text-white/80 block mb-3">{commonQuestions.attraction.label} *</label>
                <div className="space-y-2">
                  {commonQuestions.attraction.options.map((option) => (
                    <label
                      key={option}
                      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer text-sm ${
                        formData.attraction === option ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      <input
                        type="radio"
                        name="attraction"
                        value={option}
                        checked={formData.attraction === option}
                        onChange={(e) => handleChange('attraction', e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 mt-0.5 rounded-full border flex-shrink-0 flex items-center justify-center ${
                        formData.attraction === option ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-white/30'
                      }`}>
                        {formData.attraction === option && <Check size={10} className="text-[#0A0A0A]" />}
                      </div>
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Investment Budget */}
              <div>
                <label className="text-sm text-white/80 block mb-3">{commonQuestions.investmentBudget.label} *</label>
                <div className="space-y-2">
                  {commonQuestions.investmentBudget.options.map((option) => (
                    <label
                      key={option}
                      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer text-sm ${
                        formData.investmentBudget === option ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      <input
                        type="radio"
                        name="investmentBudget"
                        value={option}
                        checked={formData.investmentBudget === option}
                        onChange={(e) => handleChange('investmentBudget', e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 mt-0.5 rounded-full border flex-shrink-0 flex items-center justify-center ${
                        formData.investmentBudget === option ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-white/30'
                      }`}>
                        {formData.investmentBudget === option && <Check size={10} className="text-[#0A0A0A]" />}
                      </div>
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Region Preference */}
              <div>
                <label className="text-sm text-white/80 block mb-3">{commonQuestions.regionPreference.label}</label>
                <select
                  value={formData.regionPreference}
                  onChange={(e) => handleChange('regionPreference', e.target.value)}
                  className="input-modern bg-transparent w-full"
                >
                  <option value="" className="bg-[#121212]">Select a region...</option>
                  {commonQuestions.regionPreference.options.map((option) => (
                    <option key={option} value={option} className="bg-[#121212]">{option}</option>
                  ))}
                </select>
              </div>

              {/* Timeline */}
              <div>
                <label className="text-sm text-white/80 block mb-3">{commonQuestions.timeline.label} *</label>
                <div className="space-y-2">
                  {commonQuestions.timeline.options.map((option) => (
                    <label
                      key={option}
                      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer text-sm ${
                        formData.timeline === option ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      <input
                        type="radio"
                        name="timeline"
                        value={option}
                        checked={formData.timeline === option}
                        onChange={(e) => handleChange('timeline', e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 mt-0.5 rounded-full border flex-shrink-0 flex items-center justify-center ${
                        formData.timeline === option ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-white/30'
                      }`}>
                        {formData.timeline === option && <Check size={10} className="text-[#0A0A0A]" />}
                      </div>
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="text-sm text-white/80 block mb-2">Any additional information you'd like to share?</label>
                <textarea
                  value={formData.businessDescription}
                  onChange={(e) => handleChange('businessDescription', e.target.value)}
                  className="input-modern resize-none w-full"
                  rows={4}
                  placeholder="Tell us more about your business, goals, or any specific questions..."
                />
              </div>
            </div>
          )}

          {/* Step 4: Declaration */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2">Declaration</p>
                <h2 className="text-xl font-semibold text-white mb-6">Confirm & Submit</h2>
              </div>

              <div className="bg-white/5 rounded-xl p-6 text-sm text-white/60 space-y-4">
                <p>
                  By submitting this questionnaire, you confirm that:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>The information provided is accurate and in good faith</li>
                  <li>This information will be used solely for preparing your Discovery Call</li>
                  <li>All shared information will be treated with complete confidentiality</li>
                  <li>You understand this is an intake form and not a commitment to any package</li>
                </ul>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.declaration}
                  onChange={(e) => handleChange('declaration', e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                  formData.declaration ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-white/30'
                }`}>
                  {formData.declaration && <Check size={14} className="text-[#0A0A0A]" />}
                </div>
                <span className="text-white/80 text-sm">
                  I confirm that I have read and agree to the above declaration *
                </span>
              </label>

              {/* Summary */}
              <div className="border-t border-white/10 pt-6">
                <p className="text-sm text-white/50 mb-4">Summary of your submission:</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-white/40">Name</p>
                    <p className="text-white">{formData.fullName}</p>
                  </div>
                  <div>
                    <p className="text-white/40">Email</p>
                    <p className="text-white">{formData.email}</p>
                  </div>
                  <div>
                    <p className="text-white/40">Phone</p>
                    <p className="text-white">{formData.phone}</p>
                  </div>
                  <div>
                    <p className="text-white/40">Sector</p>
                    <p className="text-white">{sectors.find(s => s.value === formData.sector)?.label || formData.otherSector}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="btn-secondary flex items-center gap-2"
              >
                <ArrowLeft size={16} />
                Back
              </button>
            ) : (
              <div />
            )}
            
            {step < 4 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-questionnaire"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Submit Questionnaire
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
